import { useState, useEffect, useRef } from 'react'
import { AdminAlert, AdminButton, isNotBuiltYet } from '../AdminShared'
import { useAuth } from '../../../context/AuthContext'
import { SuperAdminOnly } from '../components/PermissionNotice'
import {
  adminGetAllSettings,
  adminGetAntiFarmingSettings,
  adminUpdateAntiFarmingSettings,
  adminUpdatePlatformInfo,
  adminUpdateMaintenanceMode,
  adminUpdateCisWeights,
  adminUpdateCisRewards,
  adminUpdateReferralRewards,
  adminUpdateFeatureFlags,
} from '../../../services/admin'

const DEFAULTS = {
  post: {
    cooldownMinutes: 60,
    maxPerDay: 10,
    rewardedPerDay: 5,
    minCharacters: 50,
    minWords: 10,
    duplicateSimilarityThreshold: 0.8,
  },
  comment: {
    cooldownMinutes: 5,
    maxPerDay: 100,
    rewardedPerDay: 20,
    minCharacters: 2,
    minWords: 1,
    duplicateSimilarityThreshold: 0.9,
  },
  spam: {
    maxLinks: 5,
    maxRepeatedCharacters: 6,
  },
}

const FIELD_META = {
  cooldownMinutes:              { label: 'Cooldown (minutes)',           step: 1,    min: 0 },
  maxPerDay:                    { label: 'Max Per Day',                  step: 1,    min: 0 },
  rewardedPerDay:                { label: 'Rewarded Per Day',             step: 1,    min: 0 },
  minCharacters:                { label: 'Min Characters',               step: 1,    min: 0 },
  minWords:                     { label: 'Min Words',                    step: 1,    min: 0 },
  duplicateSimilarityThreshold: { label: 'Duplicate Similarity Threshold', step: 0.05, min: 0, max: 1 },
  maxLinks:                     { label: 'Max Links',                    step: 1,    min: 0 },
  maxRepeatedCharacters:        { label: 'Max Repeated Characters',      step: 1,    min: 1 },
}

const SECTIONS = [
  { key: 'post',    label: 'Posts',    icon: 'article', cisField: 'post' },
  { key: 'comment', label: 'Comments', icon: 'comment', cisField: 'commentReceived' },
  { key: 'spam',    label: 'Spam Detection', icon: 'block' },
]

const CIS_DEFAULTS = { social: 0.4, staking: 0.4, consistency: 0.2 }
const CIS_REWARDS_DEFAULTS = {
  post: 10,
  likeReceived: 0.1,
  commentReceived: 0.5,
  streakLoginDay: 40,
  stakingFactor: 0.2,
  cptToCisRate: 40,
}
const CIS_REWARDS_FIELD_META = {
  post:            { label: 'Per Post',                  step: 0.1,  min: 0 },
  likeReceived:    { label: 'Per Like Received',         step: 0.01, min: 0 },
  commentReceived: { label: 'Per Comment Received',      step: 0.01, min: 0 },
  streakLoginDay:  { label: 'Per Login Streak Day',      step: 1,    min: 0 },
  stakingFactor:   { label: 'Staking Score Multiplier',  step: 0.01, min: 0 },
  cptToCisRate:    { label: 'CIS Awarded Per 1 CPT Converted', step: 1, min: 0.0001 },
}
const ENGAGEMENT_CIS_FIELDS = ['likeReceived', 'streakLoginDay', 'stakingFactor', 'cptToCisRate']

const REFERRAL_DEFAULTS = {
  signupReferrerCis: 100,
  signupReferredCis: 100,
  completionReferrerCis: 1900,
  completionReferredCis: 900,
  stakingReferrerCis: 3000,
}
const REFERRAL_FIELD_META = {
  signupReferrerCis:     { label: 'Referrer — On Signup' },
  signupReferredCis:     { label: 'New User — On Signup' },
  completionReferrerCis: { label: 'Referrer — Checklist Complete' },
  completionReferredCis: { label: 'New User — Checklist Complete' },
  stakingReferrerCis:    { label: 'Referrer — Staking Milestone' },
}
const FEATURE_FLAG_DEFAULTS = { staking: true, offerwall: true, leaderboard: true, referrals: true, ads: true, cptToCisConversion: false }
const FEATURE_FLAG_LABELS = {
  staking: 'Staking',
  offerwall: 'Offerwall',
  leaderboard: 'Leaderboard',
  referrals: 'Referrals',
  ads: 'Ads (OFF = shows "Coming Soon" to users)',
  cptToCisConversion: 'CPT → CIS Conversion',
}
const PLATFORM_INFO_DEFAULTS = { name: 'My Platform', description: '' }

function deepEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b)
}

const LOCK_CONFIG = 'Read-only — needs the Platform config permission'
const LOCK_CIS = 'Read-only — needs the CIS permission'

function LockNote({ children }) {
  return (
    <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">
      <span className="material-symbols-outlined text-[14px]" style={{ fontVariationSettings: "'FILL' 1" }}>lock</span>
      {children}
    </p>
  )
}

// `locked` disables every input/button inside (via <fieldset disabled>).
function Card({ title, description, locked = false, lockedLabel, children }) {
  return (
    <div className="border-4 border-on-background bg-surface p-4 sm:p-5 flex flex-col gap-4"
      style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
      <div className="flex flex-col gap-1">
        <h3 className="font-extrabold text-[13px] uppercase tracking-wider">{title}</h3>
        {description && <p className="text-[12px] text-on-surface-variant">{description}</p>}
        {locked && <LockNote>{lockedLabel}</LockNote>}
      </div>
      <fieldset disabled={locked} className="min-w-0 border-0 p-0 m-0 flex flex-col gap-4">
        {children}
      </fieldset>
    </div>
  )
}

function Toggle({ label, checked, onChange, disabled = false }) {
  return (
    <label className={`flex items-center gap-3 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
      <div
        onClick={() => { if (!disabled) onChange(!checked) }}
        className={`w-10 h-5 border-2 border-on-background relative transition-colors ${checked ? 'bg-primary-container' : 'bg-surface-container'}`}
      >
        <div className={`absolute top-0.5 w-3.5 h-3.5 bg-on-background transition-all ${checked ? 'left-[18px]' : 'left-0.5'}`} />
      </div>
      <span className="font-bold text-[12px] uppercase tracking-widest">{label}</span>
    </label>
  )
}

export function PlatformConfig() {
  const { isSuperAdmin, hasPermission } = useAuth()
  const canConfig = hasPermission('platform_config')
  const canCis = hasPermission('cis')
  const [draft, setDraft] = useState(null)
  const [saved, setSaved] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  const apiAvailable = useRef(null)

  const [platformInfoDraft, setPlatformInfoDraft] = useState(PLATFORM_INFO_DEFAULTS)
  const [platformInfoSaved, setPlatformInfoSaved] = useState(PLATFORM_INFO_DEFAULTS)
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false)
  const [maintenanceSaved, setMaintenanceSaved] = useState(false)
  const [cisWeightsDraft, setCisWeightsDraft] = useState(CIS_DEFAULTS)
  const [cisWeightsSaved, setCisWeightsSaved] = useState(CIS_DEFAULTS)
  const [cisRewardsDraft, setCisRewardsDraft] = useState(CIS_REWARDS_DEFAULTS)
  const [cisRewardsSaved, setCisRewardsSaved] = useState(CIS_REWARDS_DEFAULTS)
  const [referralDraft, setReferralDraft] = useState(REFERRAL_DEFAULTS)
  const [referralSaved, setReferralSaved] = useState(REFERRAL_DEFAULTS)
  const [flagsDraft, setFlagsDraft] = useState(FEATURE_FLAG_DEFAULTS)
  const [flagsSaved, setFlagsSaved] = useState(FEATURE_FLAG_DEFAULTS)

  const [savingPlatformInfo, setSavingPlatformInfo] = useState(false)
  const [savingMaintenance, setSavingMaintenance] = useState(false)
  const [savingCisWeights, setSavingCisWeights] = useState(false)
  const [savingCisRewards, setSavingCisRewards] = useState(false)
  const [savingReferral, setSavingReferral] = useState(false)
  const [savingFlags, setSavingFlags] = useState(false)
  const [cisWeightsError, setCisWeightsError] = useState(null)

  useEffect(() => {
    setLoading(true)
    Promise.allSettled([adminGetAllSettings(), adminGetAntiFarmingSettings()])
      .then(([allRes, antiFarmingRes]) => {
        if (allRes.status === 'fulfilled') {
          const data = allRes.value ?? {}
          apiAvailable.current = true
          const pi = { ...PLATFORM_INFO_DEFAULTS, ...data.platformInfo }
          setPlatformInfoDraft(pi); setPlatformInfoSaved(pi)
          const me = data.maintenanceMode?.enabled ?? false
          setMaintenanceEnabled(me); setMaintenanceSaved(me)
          const cw = { ...CIS_DEFAULTS, ...data.cisWeights }
          setCisWeightsDraft(cw); setCisWeightsSaved(cw)
          const cr = { ...CIS_REWARDS_DEFAULTS, ...data.cisRewards }
          setCisRewardsDraft(cr); setCisRewardsSaved(cr)
          const rr = { ...REFERRAL_DEFAULTS, ...data.referralRewards }
          setReferralDraft(rr); setReferralSaved(rr)
          const ff = { ...FEATURE_FLAG_DEFAULTS, ...data.featureFlags }
          setFlagsDraft(ff); setFlagsSaved(ff)
        } else if (!isNotBuiltYet(allRes.reason)) {
          setError(allRes.reason?.message ?? 'Failed to load platform settings')
        }

        if (antiFarmingRes.status === 'fulfilled') {
          const data = antiFarmingRes.value ?? {}
          const merged = {
            post: { ...DEFAULTS.post, ...data.post },
            comment: { ...DEFAULTS.comment, ...data.comment },
            spam: { ...DEFAULTS.spam, ...data.spam },
          }
          setSaved(merged)
          setDraft(merged)
        } else {
          setSaved(DEFAULTS)
          setDraft(DEFAULTS)
        }
      })
      .finally(() => setLoading(false))
  }, [])

  const isDirty = draft && saved && !deepEqual(draft, saved)

  const updateField = (section, field, value) => {
    setDraft(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const result = await adminUpdateAntiFarmingSettings(draft)
      const merged = {
        post: { ...DEFAULTS.post, ...result.post },
        comment: { ...DEFAULTS.comment, ...result.comment },
        spam: { ...DEFAULTS.spam, ...result.spam },
      }
      setSaved(merged)
      setDraft(merged)
      setSuccessMsg('Platform settings saved.')
      setTimeout(() => setSuccessMsg(null), 3000)
    } catch (err) {
      setError(err.message ?? 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => setDraft(saved)

  const handleSavePlatformInfo = async () => {
    setSavingPlatformInfo(true)
    setError(null)
    try {
      const result = await adminUpdatePlatformInfo(platformInfoDraft)
      const pi = { ...PLATFORM_INFO_DEFAULTS, ...(result?.value ?? platformInfoDraft) }
      setPlatformInfoSaved(pi); setPlatformInfoDraft(pi)
      setSuccessMsg('Platform info saved.')
      setTimeout(() => setSuccessMsg(null), 3000)
    } catch (err) {
      setError(err.message ?? 'Failed to save platform info')
    } finally {
      setSavingPlatformInfo(false)
    }
  }

  const handleToggleMaintenance = async (next) => {
    setMaintenanceEnabled(next)
    setSavingMaintenance(true)
    setError(null)
    try {
      await adminUpdateMaintenanceMode(next)
      setMaintenanceSaved(next)
      setSuccessMsg(next ? 'Maintenance mode enabled.' : 'Maintenance mode disabled.')
      setTimeout(() => setSuccessMsg(null), 3000)
    } catch (err) {
      setMaintenanceEnabled(maintenanceSaved)
      setError(err.message ?? 'Failed to update maintenance mode')
    } finally {
      setSavingMaintenance(false)
    }
  }

  const handleSaveCisWeights = async () => {
    const sum = Number(cisWeightsDraft.social) + Number(cisWeightsDraft.staking) + Number(cisWeightsDraft.consistency)
    if (Math.abs(sum - 1) > 0.001) {
      setCisWeightsError(`CIS weights must sum to 1.0 (currently ${sum.toFixed(2)})`)
      return
    }
    setCisWeightsError(null)
    setSavingCisWeights(true)
    setError(null)
    try {
      const result = await adminUpdateCisWeights({
        social: Number(cisWeightsDraft.social),
        staking: Number(cisWeightsDraft.staking),
        consistency: Number(cisWeightsDraft.consistency),
      })
      const cw = { ...CIS_DEFAULTS, ...(result?.value ?? cisWeightsDraft) }
      setCisWeightsSaved(cw); setCisWeightsDraft(cw)
      setSuccessMsg('CIS weights saved.')
      setTimeout(() => setSuccessMsg(null), 3000)
    } catch (err) {
      setError(err.message ?? 'Failed to save CIS weights')
    } finally {
      setSavingCisWeights(false)
    }
  }

  const handleSaveCisRewards = async () => {
    setSavingCisRewards(true)
    setError(null)
    try {
      const payload = Object.fromEntries(
        Object.keys(CIS_REWARDS_DEFAULTS).map(k => [k, Number(cisRewardsDraft[k])])
      )
      const result = await adminUpdateCisRewards(payload)
      const cr = { ...CIS_REWARDS_DEFAULTS, ...(result?.value ?? payload) }
      setCisRewardsSaved(cr); setCisRewardsDraft(cr)
      setSuccessMsg('CIS point rewards saved.')
      setTimeout(() => setSuccessMsg(null), 3000)
    } catch (err) {
      setError(err.message ?? 'Failed to save CIS point rewards')
    } finally {
      setSavingCisRewards(false)
    }
  }

  const handleSaveReferral = async () => {
    setSavingReferral(true)
    setError(null)
    try {
      const payload = Object.fromEntries(
        Object.keys(REFERRAL_DEFAULTS).map(k => [k, Number(referralDraft[k])])
      )
      const result = await adminUpdateReferralRewards(payload)
      const rr = { ...REFERRAL_DEFAULTS, ...(result?.value ?? payload) }
      setReferralSaved(rr); setReferralDraft(rr)
      setSuccessMsg('Referral rewards saved.')
      setTimeout(() => setSuccessMsg(null), 3000)
    } catch (err) {
      setError(err.message ?? 'Failed to save referral rewards')
    } finally {
      setSavingReferral(false)
    }
  }

  const handleToggleFlag = (key) => {
    setFlagsDraft(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const handleSaveFlags = async () => {
    setSavingFlags(true)
    setError(null)
    try {
      const result = await adminUpdateFeatureFlags(flagsDraft)
      const ff = { ...FEATURE_FLAG_DEFAULTS, ...(result?.value ?? flagsDraft) }
      setFlagsSaved(ff); setFlagsDraft(ff)
      setSuccessMsg('Feature flags saved.')
      setTimeout(() => setSuccessMsg(null), 3000)
    } catch (err) {
      setError(err.message ?? 'Failed to save feature flags')
    } finally {
      setSavingFlags(false)
    }
  }

  if (loading || !draft) {
    return (
      <div className="flex flex-col gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-40 bg-surface border-4 border-on-background animate-pulse" />
        ))}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {error && <AdminAlert type="error" message={error} />}
      {successMsg && <AdminAlert type="success" message={successMsg} />}
      {apiAvailable.current === false && (
        <AdminAlert
          type="info"
          message="Backend settings endpoint not reachable — showing default values. Edits won't persist until it's available."
        />
      )}

      <Card title="Maintenance Mode" description="Take the platform offline for all non-admin users." locked={!canConfig} lockedLabel={LOCK_CONFIG}>
        <Toggle
          label={maintenanceEnabled ? 'Maintenance mode is ON' : 'Maintenance mode is OFF'}
          checked={maintenanceEnabled}
          onChange={handleToggleMaintenance}
          disabled={!canConfig}
        />
        {savingMaintenance && <p className="text-[11px] text-on-surface-variant">Saving…</p>}
      </Card>

      <Card title="Platform Info" description="The platform's public name and description." locked={!canConfig} lockedLabel={LOCK_CONFIG}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Name</label>
            <input
              type="text"
              value={platformInfoDraft.name}
              onChange={e => setPlatformInfoDraft(prev => ({ ...prev, name: e.target.value }))}
              className="bg-surface border-2 border-on-background px-3 py-2 text-[14px] focus:outline-none focus:border-primary-container"
            />
          </div>
          <div className="flex flex-col gap-1 sm:col-span-2">
            <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Description</label>
            <textarea
              rows={2}
              value={platformInfoDraft.description}
              onChange={e => setPlatformInfoDraft(prev => ({ ...prev, description: e.target.value }))}
              className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container resize-y"
            />
          </div>
        </div>
        <div className="w-full sm:w-48">
          <AdminButton onClick={handleSavePlatformInfo} loading={savingPlatformInfo}
            disabled={deepEqual(platformInfoDraft, platformInfoSaved)} variant="gold">
            Save Platform Info
          </AdminButton>
        </div>
      </Card>

      <Card title="CIS Score Weights" description="Weights must sum to 1.0." locked={!canCis} lockedLabel={LOCK_CIS}>
        {cisWeightsError && <AdminAlert type="error" message={cisWeightsError} />}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {['social', 'staking', 'consistency'].map(field => (
            <div key={field} className="flex flex-col gap-1">
              <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant capitalize">{field}</label>
              <input
                type="number" step="0.05" min="0" max="1"
                value={cisWeightsDraft[field]}
                onChange={e => setCisWeightsDraft(prev => ({ ...prev, [field]: e.target.value }))}
                className="bg-surface border-2 border-on-background px-3 py-2 font-mono text-[14px] focus:outline-none focus:border-primary-container"
              />
            </div>
          ))}
        </div>
        <div className="w-full sm:w-48">
          <AdminButton onClick={handleSaveCisWeights} loading={savingCisWeights}
            disabled={deepEqual(cisWeightsDraft, cisWeightsSaved)} variant="gold">
            Save CIS Weights
          </AdminButton>
        </div>
      </Card>

      <Card title="Referral Rewards" description="CIS paid out at each stage of the referral lifecycle. Editing these takes effect on the next referral event — no redeploy needed." locked={!canCis} lockedLabel={LOCK_CIS}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Object.keys(REFERRAL_DEFAULTS).map(field => (
            <div key={field} className="flex flex-col gap-1">
              <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">
                {REFERRAL_FIELD_META[field].label}
              </label>
              <input
                type="number" min="0" step="1"
                value={referralDraft[field]}
                onChange={e => setReferralDraft(prev => ({ ...prev, [field]: e.target.value }))}
                className="bg-surface border-2 border-on-background px-3 py-2 font-mono text-[14px] focus:outline-none focus:border-primary-container"
              />
            </div>
          ))}
        </div>
        <div className="w-full sm:w-48">
          <AdminButton onClick={handleSaveReferral} loading={savingReferral}
            disabled={deepEqual(referralDraft, referralSaved)} variant="gold">
            Save Referral Rewards
          </AdminButton>
        </div>
      </Card>

      {isSuperAdmin ? (
      <Card title="Feature Flags" description="Toggle major platform features on or off.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.keys(FEATURE_FLAG_DEFAULTS).map(key => (
            <Toggle key={key} label={FEATURE_FLAG_LABELS[key] ?? key} checked={!!flagsDraft[key]} onChange={() => handleToggleFlag(key)} />
          ))}
        </div>
        {flagsDraft.cptToCisConversion && (
          <p className="text-[11px] text-on-surface-variant">
            Users can convert CPT into CIS at the rate set in Engagement &amp; Streak Rewards below.
          </p>
        )}
        <div className="w-full sm:w-48">
          <AdminButton onClick={handleSaveFlags} loading={savingFlags}
            disabled={deepEqual(flagsDraft, flagsSaved)} variant="gold">
            Save Feature Flags
          </AdminButton>
        </div>
      </Card>
      ) : (
        <SuperAdminOnly what="Feature flags" />
      )}

      <div className="border-4 border-on-background bg-surface-container-low p-5 flex flex-col gap-1"
        style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
        <p className="font-bold text-[11px] uppercase tracking-widest text-on-surface-variant">Anti-Farming Settings</p>
        <p className="text-[12px] text-on-surface-variant">
          These limits control cooldowns, daily caps, and spam/duplicate detection for posts and comments platform-wide.
        </p>
        {!canConfig && <LockNote>{LOCK_CONFIG}</LockNote>}
      </div>

      {SECTIONS.map(({ key, label, icon, cisField }) => (
        <div key={key} className="border-4 border-on-background bg-surface p-4 sm:p-5 flex flex-col gap-4"
          style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>{icon}</span>
            <h3 className="font-extrabold text-[13px] uppercase tracking-wider">{label}</h3>
          </div>
          <fieldset disabled={!canConfig} className="min-w-0 border-0 p-0 m-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.keys(draft[key]).map(field => {
              const meta = FIELD_META[field] ?? { label: field, step: 1, min: 0 }
              return (
                <div key={field} className="flex flex-col gap-1">
                  <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">{meta.label}</label>
                  <input
                    type="number"
                    step={meta.step}
                    min={meta.min}
                    max={meta.max}
                    value={draft[key][field]}
                    onChange={e => updateField(key, field, e.target.value === '' ? '' : Number(e.target.value))}
                    className="bg-surface border-2 border-on-background px-3 py-2 font-mono text-[14px] focus:outline-none focus:border-primary-container"
                  />
                </div>
              )
            })}
          </fieldset>
          {cisField && (
            <fieldset disabled={!canCis} className="min-w-0 border-0 p-0 m-0 border-t-2 border-on-background/10 pt-4">
              <div className="max-w-xs flex flex-col gap-1">
                <label className="font-bold text-[10px] uppercase tracking-widest text-primary-container flex items-center gap-1">
                  <span className="material-symbols-outlined text-[13px]">stars</span>
                  CIS Points Awarded {label === 'Posts' ? 'Per Post' : 'Per Comment Received'}
                </label>
                <input
                  type="number"
                  step={CIS_REWARDS_FIELD_META[cisField].step}
                  min={CIS_REWARDS_FIELD_META[cisField].min}
                  value={cisRewardsDraft[cisField]}
                  onChange={e => setCisRewardsDraft(prev => ({ ...prev, [cisField]: e.target.value }))}
                  className="bg-surface border-2 border-primary-container px-3 py-2 font-mono text-[14px] focus:outline-none focus:border-primary-container disabled:opacity-60"
                />
                {!canCis && <LockNote>{LOCK_CIS}</LockNote>}
              </div>
            </fieldset>
          )}
        </div>
      ))}

      <div className="border-4 border-on-background bg-surface p-4 sm:p-5 flex flex-col gap-4"
        style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>local_fire_department</span>
          <h3 className="font-extrabold text-[13px] uppercase tracking-wider">Engagement &amp; Streak Rewards</h3>
        </div>
        <p className="text-[12px] text-on-surface-variant -mt-2">
          CIS points for likes received, daily login streaks, staking, and the CPT-to-CIS conversion rate.
        </p>
        {!canCis && <LockNote>{LOCK_CIS}</LockNote>}
        <fieldset disabled={!canCis} className="min-w-0 border-0 p-0 m-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ENGAGEMENT_CIS_FIELDS.map(field => {
            const meta = CIS_REWARDS_FIELD_META[field]
            return (
              <div key={field} className="flex flex-col gap-1">
                <label className="font-bold text-[10px] uppercase tracking-widest text-primary-container flex items-center gap-1">
                  <span className="material-symbols-outlined text-[13px]">stars</span>
                  {meta.label}
                </label>
                <input
                  type="number"
                  step={meta.step}
                  min={meta.min}
                  value={cisRewardsDraft[field]}
                  onChange={e => setCisRewardsDraft(prev => ({ ...prev, [field]: e.target.value }))}
                  className="bg-surface border-2 border-primary-container px-3 py-2 font-mono text-[14px] focus:outline-none focus:border-primary-container"
                />
              </div>
            )
          })}
        </fieldset>
      </div>

      {canCis && <div className="flex gap-3">
        <div className="flex-1">
          <AdminButton onClick={handleSaveCisRewards} loading={savingCisRewards}
            disabled={deepEqual(cisRewardsDraft, cisRewardsSaved)} variant="gold">
            <span className="material-symbols-outlined text-[16px]">stars</span>
            Save CIS Point Rewards (Posts, Comments, Likes, Streak, Staking, Conversion Rate)
          </AdminButton>
        </div>
      </div>}

      {canConfig && <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <AdminButton onClick={handleSave} loading={saving} disabled={!isDirty || loading} variant="gold">
            <span className="material-symbols-outlined text-[16px]">save</span>
            Save Anti-Farming Settings
          </AdminButton>
        </div>
        <div className="sm:w-40">
          <AdminButton onClick={handleReset} disabled={!isDirty || loading || saving} variant="outline">
            Discard
          </AdminButton>
        </div>
      </div>}
    </div>
  )
}